import { SYMPTOM_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { PastSymptom } from "~/entities/medical/past_symptom";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { formatDateForHumans } from "~/util/dates";

interface PastSymptomListingItem {
    id: string;
    title: string;
    date: string | null;
}

const type = MedicalType.past_symptom;

export default function PastSymptomsIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: PastSymptomListingItem[] = useMemo(() => {
        return listMedical(type).map((i: PastSymptom) => ({
            id: i.id as string,
            title: findOption(OptionType.past_symptoms, i.option_id)?.title as string,
            date: i.occurred_on ? formatDateForHumans(i.occurred_on, "short") : null,
        }));
    }, [listMedical, findOption]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="past_symptoms">
            <Stack.Screen
                options={{
                    headerRight: () => (
                        <ShareButton title="Share past symptoms" initial={["past_symptoms", "photos"]} />
                    ),
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Past symptoms"
                    icon="search"
                    button={() => (
                        <Can ability="update" section="past_symptoms">
                            <Button
                                title="Add past symptom"
                                variant="primary"
                                size="lg"
                                href="/health-profile/past-symptoms/create"
                            />
                        </Can>
                    )}
                >
                    Document past symptoms to keep track of them.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<PastSymptomListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                hideChevron
                                subtitle={item.date}
                                href={{ pathname: `${SYMPTOM_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
